import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaAngleLeft, FaSave } from 'react-icons/fa';
import { API } from 'aws-amplify';
import { useToasts } from 'react-toast-notifications';

const GlobalProfile = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToasts();
  const [selProfile, setSelProfile] = useState({});
  
  const [newCategory, setNewCategory] = useState('');
  const [newPermission, setNewPermission] = useState({ category: '', label: '', checked: false });

  const params = {
    headers: {
      'x-api-key': process.env.REACT_APP_API_KEY,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  useEffect(() => {
    const globalPermission = async () => {
      try {
        const global_perm = await API.get('global_custom_permission_list', '/', params);
        setSelProfile(global_perm[0].customProfile);
      } catch (error) {
        console.error('Error fetching permissions:', error);
        addToast('Error fetching permissions', { appearance: 'error' });
      }
    };
    globalPermission();
  }, [addToast]);

  // Handle checkbox change
  const handleCheckboxChange = (category, permission) => {
    setSelProfile((prevProfile) => ({
      ...prevProfile,
      [category]: {
        ...prevProfile[category],
        [permission]: {
          ...prevProfile[category][permission],
          checked: !prevProfile[category][permission].checked,
        },
      },
    }));
  };

  // Function to add a new category
  const handleAddCategory = () => {
    if (!newCategory.trim()) {
      addToast('Category name cannot be empty', { appearance: 'error' });
      return;
    }

    setSelProfile((prevProfile) => ({
      ...prevProfile,
      [newCategory]: {},
    }));

    setNewCategory(''); // Reset input field
    addToast('New category added', { appearance: 'success' });
  };

  // Function to add a new permission to an existing category
  const handleAddPermission = () => {
    const { category, label, checked } = newPermission;
    
    // Check for empty category or label
    if (!category.trim() || !label.trim()) {
      addToast('Category and permission label cannot be empty', { appearance: 'error' });
      return;
    }
  
    // Check if category exists
    if (!selProfile[category]) {
      addToast('Category does not exist', { appearance: 'error' });
      return;
    }
  
    // Trim the label to remove extra spaces
    const trimmedLabel = label.trim(); // Trim spaces
  
    // Add the new permission
    setSelProfile((prevProfile) => ({
      ...prevProfile,
      [category]: {
        ...prevProfile[category],
        [trimmedLabel.replace(/\s+/g, '_').toLowerCase()]: { checked, label: trimmedLabel }, // Use trimmed label
      },
    }));
  
    // Reset input fields
    setNewPermission({ category: '', label: '', checked: false });
    addToast('New permission added', { appearance: 'success' });
  };
  

  // Function to delete a category or permission
  const handleDelete = () => {
    const { category, label } = newPermission;

    if (category.trim() && !label.trim()) {
      if (selProfile[category]) {
        setSelProfile((prevProfile) => {
          const updatedProfile = { ...prevProfile };
          delete updatedProfile[category];
          return updatedProfile;
        });
        addToast(`Category "${category}" deleted`, { appearance: 'success' });
      } else {
        addToast('Category does not exist', { appearance: 'error' });
      }
    } else if (category.trim() && label.trim()) {
      if (selProfile[category] && selProfile[category][label.replace(/\s+/g, '_').toLowerCase()]) {
        setSelProfile((prevProfile) => {
          const updatedProfile = { ...prevProfile };
          delete updatedProfile[category][label.replace(/\s+/g, '_').toLowerCase()];

          if (Object.keys(updatedProfile[category]).length === 0) {
            delete updatedProfile[category];
          }

          return updatedProfile;
        });
        addToast(`Permission "${label}" deleted from category "${category}"`, { appearance: 'success' });
      } else {
        addToast('Permission or category does not exist', { appearance: 'error' });
      }
    } else {
      addToast('Category and/or permission fields cannot be empty', { appearance: 'error' });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const filteredProfile = {};

    Object.keys(selProfile).forEach((category) => {
      const filteredPermissions = Object.keys(selProfile[category]).reduce((acc, permission) => {
        if (selProfile[category][permission].checked) {
          acc[permission] = selProfile[category][permission];
        }
        return acc;
      }, {});

      if (Object.keys(filteredPermissions).length > 0) {
        filteredProfile[category] = filteredPermissions;
      }
    });

    try {
      await API.post('global_custom_permission', '/permission', {
        body: {
          customProfile: filteredProfile,
        },
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      props.history.push('/custom_profile/');
      addToast('Saved successfully', { appearance: 'success' });
    } catch (error) {
      console.error('Error saving profile:', error);
      addToast('Error saving profile', { appearance: 'error' });
    }

    setIsLoading(false);
  };

  return !isLoading ? (
    <div className="DeviceList">
      <div className="col-md-12">
        <form key="form" onSubmit={handleSubmit}>
          <div className="row header pb-2">
            <div>Profile Permissions</div>
            <div className="col-md-10 text-right">
            <Button variant="secondary" className="mr-1 text-nowrap" onClick={() => { props.history.goBack() }}>
                Back &nbsp;
                <FaAngleLeft />
              </Button>
              <Button variant="success" className="md-9 text-right" type="submit">
                Save &nbsp;
                <FaSave />
              </Button>
            </div>
          </div>
          <div className="row div-content">
            <div className="col-md-12">
              <div className="add-category-section">
                <Form.Group controlId="add-category">
                  <Form.Label>New Category</Form.Label>
                  <Form.Control
                    type="input"
                    placeholder="New Category Name"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                  />
                  <Button 
                    variant="primary" 
                    onClick={handleAddCategory} 
                    style={{ marginTop: '5px' }} // Adjust the value as needed
                  >
                    Add Category
                  </Button>
                </Form.Group>
              </div>

              <div className="add-permission-section">
                <Form.Group controlId="add-permission-category">
                  <Form.Label>Category</Form.Label>
                  <Form.Control
                    as="select" // Changed to select dropdown
                    value={newPermission.category}
                    onChange={(e) => setNewPermission({ ...newPermission, category: e.target.value })}
                  >
                    <option value="">Select Category</option>
                    {Object.keys(selProfile).map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                <Form.Group controlId="add-permission-label">
                  <Form.Label>Permission Label</Form.Label>
                  <Form.Control
                    type="input"
                    placeholder="New Permission Label"
                    value={newPermission.label}
                    onChange={(e) => setNewPermission({ ...newPermission, label: e.target.value })}
                  />
                </Form.Group>

                <Form.Check
                  type="checkbox"
                  label="Checked"
                  checked={newPermission.checked}
                  onChange={(e) => setNewPermission({ ...newPermission, checked: e.target.checked })}
                />

                <Button style={{ marginTop: '5PX' }} variant="primary" onClick={handleAddPermission}>
                  Add Permission
                </Button>
                &nbsp;
                <Button style={{ marginTop: '5PX' }} variant="danger" onClick={handleDelete}>
                  Delete
                </Button>
              </div>

              <div className="p-3">
                <div className="col-md-12">
                  <div className="row px-3">
                    <div className="col-md-12">
                      <div className="row">
                        {Object.keys(selProfile).map((category, i) => (
                          <div key={`category-${i}`} className="col-md-3">
                            <h4>{category}</h4>
                            <div>
                              {Object.keys(selProfile[category]).map((permission, j) => (
                                <div key={`permission-${category}-${j}`}>
                                  <Form.Check
                                    type="checkbox"
                                    id={permission}
                                    label={selProfile[category][permission].label}
                                    checked={selProfile[category][permission].checked}
                                    onChange={() => handleCheckboxChange(category, permission)}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  ) : (
    ''
  );
};

export default GlobalProfile;
